import { ehIdioma, IDIOMA_PADRAO, type Idioma, idiomaDaEtiqueta } from '@empire/shared'
import { createElement, Fragment, type ReactNode, useSyncExternalStore } from 'react'
import { ES_CL } from '../i18n/es-CL'

export type { Idioma }

/**
 * Tradução da interface.
 *
 * **A chave é a própria frase em português.** Não há código inventado
 * (`settings.title`) e isso é decisão, não preguiça: o código continua legível
 * sem consultar catálogo nenhum, e frase sem tradução cai em português em vez
 * de aparecer como chave crua na tela do cliente. O preço é que reescrever o
 * texto em português derruba a tradução daquela frase — que é um defeito
 * visível e barato, e não um texto sumido.
 *
 * O catálogo é só do espanhol. Português não tem arquivo porque é o original:
 * um dicionário `'Salvar' → 'Salvar'` seria uma cópia inteira para manter em
 * sincronia sem ganhar nada.
 */
const CATALOGOS: Record<Idioma, Record<string, string>> = {
  'pt-BR': {},
  'es-CL': ES_CL,
}

const CHAVE = 'empire:idioma'

/**
 * O que o navegador pede, quando não há escolha gravada.
 *
 * `languages` e não `language`: quem tem espanhol como segunda opção declarada
 * também prefere espanhol a português, e o primeiro item é só o topo dessa
 * lista. Percorrer na ordem respeita a preferência de quem configurou.
 */
function doNavegador(): Idioma {
  const etiquetas = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const etiqueta of etiquetas) {
    const encontrado = idiomaDaEtiqueta(etiqueta)
    if (encontrado) return encontrado
  }
  return IDIOMA_PADRAO
}

/**
 * Escolha gravada, se houver; senão o navegador.
 *
 * O `localStorage` aqui não é a fonte da verdade — ela é a coluna `idioma` do
 * usuário, no banco. Ele é a cópia local que permite acertar o idioma **antes
 * do primeiro quadro**, sem esperar a sessão carregar. Sem essa cópia, login,
 * convite e a tela de carregamento nasceriam em português para um chileno, e
 * a página trocaria de idioma na frente dele.
 */
export function idiomaPreferido(): Idioma {
  const salvo = localStorage.getItem(CHAVE)
  return ehIdioma(salvo) ? salvo : doNavegador()
}

let atual: Idioma = typeof window === 'undefined' ? IDIOMA_PADRAO : idiomaPreferido()
const ouvintes = new Set<() => void>()

/**
 * A etiqueta no `<html>` não é enfeite: é o que faz o corretor ortográfico do
 * celular, a leitura por voz e a tradução automática do navegador tratarem a
 * página pelo idioma certo.
 */
export function aplicarIdioma(idioma: Idioma) {
  document.documentElement.lang = idioma
}

export function lerIdioma(): Idioma {
  return atual
}

/**
 * Troca o idioma da sessão. Não fala com o servidor — quem grava é a tela de
 * Configurações, que precisa saber se a gravação deu certo. Aqui só vale para
 * o que está na frente da pessoa.
 */
export function trocarIdioma(idioma: Idioma, { lembrar = true } = {}) {
  if (idioma === atual) return
  atual = idioma
  if (lembrar) localStorage.setItem(CHAVE, idioma)
  aplicarIdioma(idioma)
  for (const avisar of ouvintes) avisar()
}

function assinar(avisar: () => void) {
  ouvintes.add(avisar)
  return () => {
    ouvintes.delete(avisar)
  }
}

/** Idioma corrente, com re-render quando muda. */
export function useIdioma(): Idioma {
  return useSyncExternalStore(assinar, lerIdioma, () => IDIOMA_PADRAO)
}

/**
 * Traduz, e substitui `{nome}` pelos valores passados.
 *
 * As chaves entre chaves são do texto de interface e não têm relação com
 * `{primeiro_nome}` das mensagens ao cliente (`aplicarVariaveis`, no shared):
 * aquilo é conteúdo que o usuário escreve, isto é rótulo que nós escrevemos.
 *
 * É função de módulo, e não hook, para valer também fora de componente —
 * `format.ts` e `csv.ts` precisam dela. A assinatura de subscrição fica com
 * `useIdioma`; o app inteiro é remontado quando o idioma troca (ver
 * `main.tsx`), então não existe texto velho sobrando em tela.
 */
export function t(frase: string, vars?: Record<string, string | number>): string {
  const traduzida = CATALOGOS[atual][frase] ?? frase
  if (!vars) return traduzida
  return traduzida.replace(/\{(\w+)\}/g, (cru, chave: string) =>
    chave in vars ? String(vars[chave]) : cru,
  )
}

/**
 * Como `t`, mas os valores podem ser JSX — negrito, link, nome destacado.
 *
 * Existe porque a alternativa é picar a frase em pedaços (`'Quem é'`,
 * `'enxerga apenas…'`) para caber o `<strong>` no meio, e pedaço de frase não
 * se traduz: a ordem das palavras muda de um idioma para o outro, e quem
 * traduz vê três fragmentos sem saber que formavam uma frase só. Aqui a frase
 * continua inteira no catálogo e o marcador `{quem}` viaja com ela para onde
 * o espanhol precisar dele.
 */
export function tRico(frase: string, vars: Record<string, ReactNode>): ReactNode {
  const traduzida = CATALOGOS[atual][frase] ?? frase
  const pedacos = traduzida.split(/(\{\w+\})/g).map((pedaco, indice) => {
    const chave = /^\{(\w+)\}$/.exec(pedaco)?.[1]
    const valor = chave && chave in vars ? vars[chave] : pedaco
    // A chave é o índice de propósito: a frase é fixa dentro de um idioma, e
    // os pedaços nunca são reordenados nem removidos em tempo de execução.
    return createElement(Fragment, { key: indice }, valor)
  })
  return createElement(Fragment, null, ...pedacos)
}
